import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { store } from '../redux/store';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Home from './Home';
import Search from './Search';
import useTrackRouteChanges from '../hooks/useTrackRouteChanges';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchUser, setUserData } from '../redux/slices/userSlice';
import { useUser } from '../hooks/useUser';
import Account from './Account';
import Logs from './Logs';
import Admin from './Admin';
import Users from './Users';
import Tags from './Tags';
import CardInput from './CardInput';
import GlobalNoticeModal from "./GlobalNoticeModal";

interface BusinessCardsProps {
  userKey: number;
  companyKey: number;
  companyApiKey: string;
}

interface BusinessCardsRoutesProps {
  userKey: number;
  userToken: string;
  companyKey: number;
  companyApiKey: string;
}

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const rawValue = parts.pop()?.split(";").shift();
    return rawValue ? decodeURIComponent(rawValue) : null;
  }
  return null;
};

const BusinessCards: React.FC<BusinessCardsProps> = (props) => {
  const token = getCookie("cards_token");
  const userId = getCookie("cards_user_id");

  if (!token || !userId) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: "#111",
        color: "#fff",
        fontFamily: "sans-serif"
      }}>
        <h1 style={{ fontSize: "2rem", marginBottom: "1rem" }}>Cannot access</h1>
        <p style={{ color: "#aaa" }}>Please log in through the main website to access this page.</p>
      </div>
    );
  }

  const userKey = Number(userId);
  const userToken = "7e16b5527c77ea58";
  const companyKey = props.companyKey || 2;
  const companyApiKey = props.companyApiKey || "09df728269";

  if (!companyApiKey) {
    return <div>Missing company API Key</div>;
  }

  return (
    <Provider store={store}>
      <BrowserRouter>
        <BusinessCardsRoutes userKey={userKey} userToken={userToken} companyKey={companyKey} companyApiKey={companyApiKey} />
      </BrowserRouter>
    </Provider>
  );
}

export default BusinessCards;

const BusinessCardsRoutes: React.FC<BusinessCardsRoutesProps> = ({ userKey: propUserKey, userToken: propUserToken, companyKey: propCompanyKey, companyApiKey: propCompanyApiKey }) => {
  useTrackRouteChanges();
  const dispatch = useAppDispatch();
  const { userKey: storedUserKey, userToken: storedUserToken, companyKey: storedCompanyKey /*loading: userLoading, error: userError*/ } = useUser();
  const indexUnauthorized = useAppSelector((state) => state.user.unauthorized);

  useEffect(() => {
    // Step 1: Ensure props contain userKey, userToken, and companyKey. Then set Credentials
    if (propUserKey && propUserToken && propCompanyKey) {
      dispatch(setUserData({ userKey: propUserKey, userToken: propUserToken, companyKey: propCompanyKey }));
    }
  }, [dispatch, propUserKey, propUserToken, propCompanyKey]);

  useEffect(() => {
    // Step 2: Fetch user data if storedUserKey and storedToken match props
    if (storedUserKey === propUserKey && storedUserToken === propUserToken && storedCompanyKey === propCompanyKey) {
      dispatch(fetchUser());
    }
  }, [dispatch, storedUserKey, storedUserToken, storedCompanyKey, propUserKey, propUserToken, propCompanyKey]);

  if (indexUnauthorized) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: "#111",
        color: "#fff",
        fontFamily: "sans-serif"
      }}>
        <h1 style={{ fontSize: "2rem", marginBottom: "1rem" }}>Cannot access</h1>
        <p style={{ color: "#aaa" }}>Please log in through the main website to access this page.</p>
      </div>
    );
  }

  return (
    <>
    {/* notify() / askConfirm() のモーダルの表示先（全画面で1つ） */}
    <GlobalNoticeModal />
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/camera/:cardKeyParam?" element={<CardInput />} />
      <Route path="/search" element={<Search />} />
      <Route path="/account" element={<Account />} />
      <Route path="/logs" element={<Logs />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/users" element={<Users />} />
      <Route path="/tags" element={<Tags />} />
      <Route path="*" element={<Home />} />
    </Routes>
    </>
  );
}
